<template>
    <div
        class="md-nbr-demographics standard"
        :style="{ marginBottom }"
        v-if="isShow"
    >
        <module-box
            class="demographics-content"
            :fullColumn="fullColumn"
            :mobileNoMargin="mobileNoMargin"
            noPadding
            noMargin
        >
            <div v-if="title" class="mg-title" v-html="formatFontSize(title, '--mg-title-size')"></div>
            <AttomDesc :size="14" />
            <div
                class="demographics-info"
                :class="{
                    'show-empty': infoEmpty
                }"
            >
                <div class="empty-data" v-show="infoEmpty">
                    <site-img
                        src="//cdn.chime.me/image/fs01/agnentinfo/20180305/22/original_5294514866269879.png"
                    />
                    <p class="text">
                        {{ $t('md-nbr-demographics.comingSoon') }}
                    </p>
                </div>
                <div
                    class="info-item common"
                    v-for="item in infoData"
                    :key="item.title"
                >
                    <h2 class="title">{{ item.title }}</h2>
                    <div class="content">
                        <span class="value">{{ item.value }}</span>
                        <div class="desc" v-html="item.desc"></div>
                    </div>
                </div>
                <div class="info-item gender" v-if="!infoEmpty">
                    <h2 class="title">
                        {{ $t('md-nbr-demographics.gender') }}
                    </h2>
                    <div class="content">
                        <nbr-chart
                            class="info-gender-chart"
                            type="pie"
                            :options="genderOptions"
                        />
                    </div>
                </div>
                <div class="info-item education">
                    <h2 class="title">
                        {{ $t('md-nbr-demographics.education') }}
                    </h2>
                    <div class="content">
                        <div class="education-bar">
                            <span
                                :style="{
                                    backgroundColor: item.color,
                                    width: `${item.value}%`
                                }"
                                v-for="item in educationData"
                                :key="item.text"
                            ></span>
                        </div>
                        <div class="education-legend">
                            <div
                                class="legend-item"
                                v-for="(item, index) in educationData"
                                :key="item.text"
                            >
                                <span
                                    class="percent"
                                    :class="{ light: index === 0 }"
                                >{{ Number(item.value).toFixed(0) }}%</span>
                                <span class="text">{{ item.text }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="demographics-charts">
                <div class="top-charts">
                    <nbr-chart
                        class="demographics-age-chart"
                        type="bar"
                        :subTitle="
                            $t('md-nbr-demographics.median', {
                                median: ageMedian
                            })
                        "
                        :options="ageOptions"
                        :style="!showRaceModule && { width: '100%' }"
                        :title="$t('md-nbr-demographics.age')"
                    />
                    <nbr-chart
                        v-if="showRaceModule"
                        class="demographics-race-chart"
                        type="pie"
                        :options="raceOptions"
                        :title="$t('md-nbr-demographics.race')"
                    />
                </div>
                <div class="bottom-charts">
                    <nbr-chart
                        class="demographics-income-chart"
                        type="bar"
                        :subTitle="
                            $t('md-nbr-demographics.average', {
                                incomeAverage: incomeAverage
                            })
                        "
                        :options="incomeOptions"
                        :title="$t('md-nbr-demographics.annualIncome')"
                    />
                    <nbr-chart
                        class="demographics-employment-chart"
                        type="pie"
                        :options="employmentOptions"
                        :title="$t('md-nbr-demographics.employment')"
                    />
                </div>
            </div>
        </module-box>
    </div>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { usePageStore } from '@/store';
import common from '../index';

import nbrChart from '../../../components/nbr-chart/standard.vue';
import { numberFormat } from '../../../common/format';
import { commonPie, commonBar } from '../method';
export default {
    mixins: [common],
    data: function() {
        return {
            title: '',
            infoData: [],
            educationData: [],
            infoEmpty: true,
            genderOptions: null,
            ageOptions: null,
            raceOptions: null,
            incomeOptions: null,
            employmentOptions: null,
            ageMedian: '',
            incomeAverage: ''
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            'page': store => store.page || {},
            'currencySymbol': store => store.currency.symbol,
            'usdCurrencyRate': store => store.usdCurrencyRate
        }),
        
    },
    methods: {
        resetdata: function() {
            this.infoData = [];
            this.educationData = [];
            this.genderOptions = null;
            this.ageOptions = null;
            this.raceOptions = null;
            this.incomeOptions = null;
            this.employmentOptions = null;
        },
        setChartData(data) {
            this.setInfoData(data);
            data.education && this.setEducationData(data.education);
            data.gender && this.setGenderOptions(data.gender);
            data.age && this.setAgeOptions(data.age);
            data.race && this.setRaceOptions(data.race);
            data.income && this.setIncomeOptions(data.income);
            data.employment && this.setEmploymentOptions(data.employment);
        },
        setInfoData(data) {
            this.infoEmpty = false;
            let _this = this
            const infoData = [
                {
                    get title() { 
                        return _this.$t('md-nbr-demographics.population')
                    },
                    value: numberFormat(
                        data.population.populationCurrent || data.population.population2010,
                        true,
                        1
                    ),
                    get desc() {
                        if (data.population.populationCurrent) {
                            return _this.$t('md-nbr-demographics.in2020', {
                                population: numberFormat(
                                    data.population.populationCurrent,
                                    true
                                )
                            })
                        } else {
                            return _this.$t('md-nbr-demographics.in2010', {
                                population: numberFormat(
                                    data.population.population2010,
                                    true
                                )
                            })
                        }
                    }
                },
                {
                    get title() { 
                        return _this.$t('md-nbr-demographics.density')
                    },
                    value: numberFormat(
                        data.population.populationDensity,
                        true,
                        1
                    ),
                    get desc() { 
                        return _this.$t('md-nbr-demographics.perSquareMile')
                    }
                },
                {
                    get title() { 
                        return _this.$t('md-nbr-demographics.households')
                    },
                    value: numberFormat(
                        data.households.householdsTotal,
                        true,
                        1
                    ),
                    get desc() {
                        return _this.$t('md-nbr-demographics.percentWithChildren', {
                            percent: Number(
                                data.households.householdsOccupancyChildren
                            ).toFixed(0)
                        })
                    }
                }
            ];
            this.infoData = infoData;
        },
        setEducationData(data) {
            let _this = this
            const educationData = [
                {
                    value: data.educationAssociate,
                    color: '#03c9ff',
                    get text() {
                        return _this.$t('md-nbr-demographics.associate')
                    }
                },
                {
                    value: data.educationBachelor,
                    color: '#6bcbff',
                    get text() {
                        return _this.$t('md-nbr-demographics.bachelor')
                    }
                },
                {
                    value: data.educationGraduate,
                    color: '#b2e5ff',
                    get text() {
                        return _this.$t('md-nbr-demographics.graduate')
                    }
                },
                {
                    value: data.educationHighschool,
                    color: '#ceedfe',
                    get text() {
                        return _this.$t('md-nbr-demographics.highschool')
                    }
                }
            ];
            this.educationData = educationData;
        },
        setGenderOptions(data) {
            const colors = ['#03c9ff', '#cdecfd'];
            const seriesData = [
                {
                    name: 'Female',
                    value: data.genderFemale
                },
                {
                    name: 'Male',
                    value: data.genderMale
                }
            ];
            seriesData.map((item, index) => {
                item.itemStyle = {
                    color: colors[index]
                };
                item.emphasis = {
                    itemStyle: {
                        color: colors[index]
                    }
                };
            });
            let context = this;
            const baseOption = {
                legend: {
                    top: 15,
                    bottom: 'auto',
                    left: 60,
                    itemGap: 10,
                    itemWidth: 0,
                    itemHeight: 0,
                    orient: 'vertical',
                    formatter: function(name) {
                        let label = context.$t(
                            `md-nbr-demographics.${name.toLowerCase()}`
                        );
                        return (
                            '{a|' +
                            Number(data['gender' + name]).toFixed(1) +
                            '%}{b|' +
                            label +
                            '}'
                        );
                    },
                    textStyle: {
                        fontSize: 12,
                        padding: [0, 0, 0, 5],
                        rich: {
                            a: {
                                color: '#515666',
                                width: 37,
                                fontFamily: 'font-medium'
                            },
                            b: {
                                color: '#a0a3af',
                                padding: [0, 0, 0, 6],
                                width: 48,
                                fontFamily: 'font-medium'
                            }
                        }
                    },
                    data: [
                        {
                            name: 'Female'
                        },
                        {
                            name: 'Male',
                            textStyle: {
                                rich: {
                                    a: {
                                        color: '#a0a3af'
                                    }
                                }
                            }
                        }
                    ]
                },
                series: [
                    {
                        center: [30, 34],
                        radius: [20, 30],
                        data: seriesData
                    }
                ]
            };
            const chartOption = {
                baseOption: baseOption,
                media: [
                    {
                        query: {
                            minWidth: 300
                        },
                        option: {
                            legend: {
                                top: 24,
                                bottom: 'auto',
                                left: 76,
                                itemGap: 25
                            }
                        }
                    },
                    {
                        query: {
                            maxWidth: 300
                        },
                        option: {
                            legend: {
                                top: 15,
                                bottom: 'auto',
                                left: 60,
                                itemGap: 10
                            }
                        }
                    }
                ]
            };
            this.genderOptions = chartOption;
        },
        setAgeOptions(data) {
            this.ageMedian = Number(data.ageMedian).toFixed(0);
            const colors = [
                '#e7f6ff',
                '#ceedfe',
                '#ceedfe',
                '#b2e5ff',
                '#6bcbff',
                '#03c9ff'
            ];
            const yAxisData = [
                '0-11',
                '12-24',
                '25-34',
                '35-44',
                '45-64',
                '65+'
            ];
            const seriesData = [
                Number(data.ageY0to5) + Number(data.ageY6to11),
                Number(data.ageY12to17) + Number(data.ageY18to24),
                Number(data.ageY25to34),
                Number(data.ageY35to44),
                Number(data.ageY45to54) + Number(data.ageY55to64),
                Number(data.ageY65to74) +
                    Number(data.ageY75to84) +
                    Number(data.ageY85up)
            ];
            const chartOption = commonBar(colors, yAxisData, seriesData);
            this.ageOptions = chartOption;
        },
        setRaceOptions(data) {
            const colors = [
                '#f0454c',
                '#ff827b',
                '#ffb8b4',
                '#fcd7d2',
                '#fdece9'
            ];
            data.raceOther =
                100 -
                data.raceAsian -
                data.raceBlack -
                data.raceWhite -
                data.raceNative;
            const seriesData = [
                {
                    name: 'Asian',
                    value: data.raceAsian
                },
                {
                    name: 'Black',
                    value: data.raceBlack
                },
                {
                    name: 'Other',
                    value: data.raceOther
                },
                {
                    name: 'White',
                    value: data.raceWhite
                },
                {
                    name: 'Native',
                    value: data.raceNative
                }
            ];
            seriesData.sort(function(a, b) {
                return b.value - a.value;
            });
            const chartOption = commonPie(colors, 'race', seriesData, data, {
                context: this
            });
            const chartMedia = chartOption.media;
            chartMedia.length = 1;
            chartMedia[0].option.series = [
                {
                    center: ['50%', '34%'],
                    radius: ['37%', '53%']
                }
            ];
            chartMedia[0].option.legend.itemGap = 20;
            delete chartMedia[0].option.legend.left;
            chartMedia[0].option.legend.textStyle = {
                rich: {
                    a: {
                        align: 'left',
                        width: 60,
                        padding: [-20, 0, 0, 0]
                    },
                    b: {
                        align: 'left',
                        padding: [-20, 0, 0, 0]
                    }
                }
            };
            this.raceOptions = chartOption;
        },
        setIncomeOptions(data) {
            this.incomeAverage =
                `${this.currencySymbol} ` +
                numberFormat(data.incomeAverage, true);
            const colors = [
                '#e7ffe3',
                '#bff6c5',
                '#96efb4',
                '#65e9a1',
                '#2cd781',
                '#20c472',
                '#19ab63'
            ];
            const baseDollarNumber = [
                35000, 50000, 75000, 100000, 150000, 200000
            ];
            //  Interval multiplied by exchange rate
            if (!this.usdCurrencyRate) {
                this.usdCurrencyRate = 1;
            }
            const newCurrencyNumber = baseDollarNumber.map(item => {
                //  in the case of M,  just keep 1 decimal places
                let target = numberFormat(item * this.usdCurrencyRate, true, 1);
                if (target.indexOf('M') > -1) {
                    return target;
                } else {
                    return numberFormat(item * this.usdCurrencyRate, true, 0);
                }
            });
            const yAxisData = [
                this.currencySymbol + `0-${newCurrencyNumber[0]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[0]}-${newCurrencyNumber[1]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[1]}-${newCurrencyNumber[2]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[2]}-${newCurrencyNumber[3]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[3]}-${newCurrencyNumber[4]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[4]}-${newCurrencyNumber[5]}`,
                this.currencySymbol + `${newCurrencyNumber[5]}+`
            ];
            const seriesData = [
                Number(data.incomePercent0to15) +
                    Number(data.incomePercent15to25) +
                    Number(data.incomePercent25to35),
                Number(data.incomePercent35to50),
                Number(data.incomePercent50to75),
                Number(data.incomePercent75to100),
                Number(data.incomePercent100to125) +
                    Number(data.incomePercent125to150),
                Number(data.incomePercent150to200),
                Number(data.incomePercent200up)
            ];
            const chartOption = commonBar(colors, yAxisData, seriesData);
            this.incomeOptions = chartOption;
        },
        setEmploymentOptions(data) {
            const colors = [
                '#ffa600',
                '#fab800',
                '#f7da46',
                '#fbe889',
                '#faefc0',
                '#fef8e4'
            ];
            const newData = [];
            const seriesData = [];
            let other = 100;
            for (let name in data) {
                newData.push({
                    name: name.replace('employment', ''),
                    value: Number(data[name])
                });
            }
            newData.sort(function(a, b) {
                return b.value - a.value;
            });
            for (let i = 0; i < 5; i++) {
                seriesData.push(newData[i]);
                other = other - newData[i].value;
            }
            seriesData.push({
                name: 'Other',
                value: other
            });
            data['employmentOther'] = other;
            seriesData.sort(function(a, b) {
                return b.value - a.value;
            });
            const chartOption = commonPie(
                colors,
                'employment',
                seriesData,
                data,
                {
                    context: this
                }
            );
            this.employmentOptions = chartOption;
        }
    },
    components: {
        nbrChart
    }
};
</script>

<style lang="scss">
@import '../../../theme/media.scss';
@import '@/theme/mixin.scss';
.md-nbr-demographics.standard {
    width: 100%;
    background-color: #fafafd;
    .nbr-chart .title-wrap {
        padding: 0 20px;
        @include media-800 {
            padding: 0 15px;
        }
    }
    @media (min-width: 601px) {
        padding: 40px;
    }
    @media (max-width: 600px) {
        padding: 20px 15px;
    }
    .mg-title {
        text-transform: uppercase;
        padding-bottom: 10px;
    }
    .attom-desc {
        padding-bottom: 20px;
    }
    .demographics-info {
        border-radius: 4px;
        background-color: #fff;
        box-shadow: 0 1px 5px 0 rgba(0, 10, 30, 0.1);
        padding: 5px 0;
        display: flex;
        flex-wrap: wrap;
        position: relative;
        min-height: 159px;
        .content {
            .nbr-chart {
                .title-wrap {
                    border-bottom: none;
                }
            }
        }
        .empty-data {
            z-index: 1;
            position: absolute;
            max-width: 50%;
            width: 150px;
            height: auto;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            img {
                width: 100%;
            }
            .text {
                text-align: center;
                margin-top: 15px;
                color: #c8c8c8;
            }
        }
        &.show-empty {
            .info-item {
                visibility: hidden;
            }
        }
        .info-item {
            display: flex;
            min-width: 210px;
            position: relative;
            height: 149px;
            flex: 1;
            flex-direction: column;
            padding: 20px 0 20px 30px;
            .title {
                font-size: 18px;
                line-height: 20px;
                font-family: var(--font-medium);
                color: var(--color-text);
            }
            .content {
                margin-top: 15px;
                padding-right: 30px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex: 1;
                .value {
                    font-size: 28px;
                    line-height: 30px;
                    color: #03c9ff;
                }
                .desc {
                    margin-top: 11px;
                    font-size: 12px;
                    color: var(--color-disable);
                    .percent {
                        font-family: var(--font-medium);
                    }
                }
            }
            &::after {
                position: absolute;
                content: '';
                width: 1px;
                height: 90px;
                background-color: var(--color-border);
                right: 0;
                top: 34px;
            }
            &.gender {
                flex: 1;
                .content {
                    .info-gender-chart {
                        height: 74px;
                        flex: 1;
                        box-shadow: none;
                        .title-wrap {
                            border-bottom: none;
                        }
                    }
                }
            }
            &.education {
                flex: 1;
                &::after {
                    display: none;
                }
                .content {
                    padding-right: 0;
                    width: 100%;
                    .education-bar {
                        width: calc(100% - 30px);
                        height: 10px;
                        border-radius: 4px;
                        overflow: hidden;
                        display: flex;
                    }
                    .education-legend {
                        width: 100%;
                        margin-top: 9px;
                        display: flex;
                        flex-wrap: wrap;
                        .legend-item {
                            width: calc((100% - 20px) / 2);
                            font-size: 12px;
                            color: var(--color-disable);
                            line-height: 1.67;
                            display: flex;
                            flex-wrap: wrap;
                            .percent {
                                font-family: var(--font-medium);
                                width: 27px;
                                margin-right: 6px;
                            }
                            .light {
                                color: var(--color-label);
                            }
                        }
                    }
                }
            }
        }
    }
    .demographics-charts {
        .top-charts,
        .bottom-charts {
            display: flex;
            justify-content: space-between;
        }
        .demographics-age-chart,
        .demographics-income-chart {
            width: 57%;
        }
        .demographics-age-chart,
        .demographics-race-chart {
            height: 377px;
        }
        .demographics-race-chart,
        .demographics-employment-chart {
            width: 41%;
        }
        .demographics-income-chart,
        .demographics-employment-chart {
            height: 422px;
        }
        .nbr-chart {
            margin-top: 20px;
            .title {
                color: var(--color-text);
                border-color: #eeeeee;
                .sub-title {
                    margin-left: 20px;
                    font-size: 14px;
                    font-family: var(--font-normal);
                    color: var(--color-disable);
                }
            }
        }
    }

    @media (max-width: 1148px) { 
        .demographics-info {
            .info-item.education {
                border-top: 1px solid var(--color-border);
            }
            .info-item {
                &:nth-child(5):after {
                    display: none;
                }
            }
        }
    }

    @media (max-width: 934px) {
        .demographics-info {
            .info-item.gender {
                border-top: 1px solid var(--color-border);
            }
            .info-item.education {
                border-top: 1px solid var(--color-border);
            }
            .info-item {
                &:nth-child(4):after {
                    display: none;
                }
                &:nth-child(5):after {
                    display: block;
                }
            }
        }
    }

    @media (max-width: 724px) {
        .info-item {
            &:nth-child(4) {
                border-top: 1px solid var(--color-border);
            }
            &:nth-child(4):after {
                display: block !important;
            }
            &:nth-child(3):after, &:nth-child(5):after {
                display: none !important;
            }
        }

    }

    @include media-800 {
        .demographics-charts {
            .top-charts,
            .bottom-charts {
                flex-direction: column;
                .nbr-chart {
                    width: 100%;
                }
            }
        }
    }
    @include media-600 {
        background-color: #f0f1f5;
        .mg-container {
            margin: 0;
            width: 100%;
        }
        .mg-title {
            width: 100%;
            padding: 25px 0 0 15px;
            background-color: #fff;
        }
        .attom-desc {
            padding: 10px 15px 25px;
            background-color: #fff;
        }
        .demographics-info {
            border-top: 1px solid var(--color-border);
            box-shadow: none;
            padding: 0;
            min-height: 110px;
            .info-item {
                min-width: 50%;
                height: 110px;
                padding: 20px 0 20px 15px;
                &:nth-child(3) {
                    border-top: none;
                }
                &:nth-child(3):after {
                    display: none;
                }
                &:nth-child(4) {
                    border-top: 1px solid var(--color-border);
                }
                &:nth-child(4):after {
                    display: block;
                }
                .title {
                    font-size: 13px;
                    line-height: 14px;
                }
                .content {
                    margin-top: 12px;
                    padding-right: 0;
                    .value {
                        font-size: 22px;
                        line-height: 22px;
                    }
                    .desc {
                        margin-top: 0;
                    }
                }
                &::after {
                    height: 50px;
                    top: 30px;
                }
                &.gender {
                    width: 100%;
                    height: 135px;
                    .content {
                        .info-gender-chart {
                            height: 68px;
                        }
                    }
                    &::after {
                        display: none;
                    }
                }
                &.education {
                    width: 100%;
                    min-height: 135px;
                    height: auto;
                    .content .education-legend .legend-item {
                        width: 80px;
                        flex: 1;
                        .percent {
                            display: block;
                            width: 100%;
                        }
                    }
                }
            }
        }
        .demographics-charts {
            margin: 0;
            .nbr-chart {
                box-shadow: none;
                margin-top: 0;
                border-top: 1px solid var(--color-border);
                border-radius: 0;
            }
            .demographics-age-chart {
                height: 345px;
            }
            .demographics-income-chart {
                height: 390px;
            }
            .demographics-race-chart {
                height: 460px;
            }
            .demographics-employment-chart {
                height: 500px;
            }
        }
    }
}
</style>
